/**
 * Componentes do Dashboard avançado da ConvertPix.
 * Cada bloco exporta um componente nomeado pra ser composto no Dashboard.
 */
import { Link } from "react-router-dom";
import {
  TrendingUp,
  TrendingDown,
  Activity,
  Wifi,
  WifiOff,
  Zap,
  Clock,
  AlertTriangle,
  CheckCircle2,
  Send,
  XCircle,
  ChevronRight,
  Hourglass,
  Package,
  Plug,
} from "lucide-react";
import { formatCurrency, formatNumber, formatRelative } from "@/lib/format";

/* =============================================================
   PERIOD FILTER
   ============================================================= */
export function PeriodFilter({ value, onChange }) {
  const opts = [
    { key: "7d", label: "7 dias" },
    { key: "30d", label: "30 dias" },
    { key: "90d", label: "90 dias" },
    { key: "all", label: "Tudo" },
  ];
  return (
    <div
      className="inline-flex p-1 rounded-lg border border-white/[0.06] bg-cp-card/40"
      data-testid="period-filter"
    >
      {opts.map((o) => (
        <button
          key={o.key}
          onClick={() => onChange(o.key)}
          data-testid={`period-${o.key}`}
          className={`px-3 py-1.5 text-xs font-medium rounded-md transition ${
            value === o.key
              ? "bg-cp-primary/15 text-cp-primary-light"
              : "text-slate-400 hover:text-white"
          }`}
        >
          {o.label}
        </button>
      ))}
    </div>
  );
}

/* =============================================================
   HERO KPI — Faturamento mês + comparativo
   ============================================================= */
export function HeroKPI({ hero }) {
  const delta = hero?.month_delta_pct;
  const positive = delta != null && delta >= 0;
  const showDelta = delta != null && hero?.value_recovered_prev_month > 0;
  return (
    <div
      className="cp-panel relative overflow-hidden p-6 md:p-8"
      data-testid="hero-kpi"
    >
      <div className="absolute -top-32 -right-32 h-64 w-64 bg-cp-primary/10 blur-[100px] rounded-full" />
      <div className="relative">
        <div className="text-[11px] uppercase tracking-wider text-cp-primary-light font-semibold mb-3">
          Faturamento recuperado este mês
        </div>
        <div className="flex items-end gap-4 flex-wrap">
          <div
            className="font-heading text-4xl md:text-6xl font-bold tracking-tighter tabular-nums text-white"
            data-testid="hero-value"
          >
            {formatCurrency(hero?.value_recovered_month || 0)}
          </div>
          {showDelta && (
            <div
              data-testid="hero-delta"
              className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-sm font-semibold ${
                positive
                  ? "bg-cp-primary/15 text-cp-primary-light"
                  : "bg-cp-warning/15 text-cp-warning"
              }`}
            >
              {positive ? (
                <TrendingUp className="h-4 w-4" />
              ) : (
                <TrendingDown className="h-4 w-4" />
              )}
              {positive ? "+" : ""}
              {delta.toFixed(1)}% vs mês anterior
            </div>
          )}
        </div>
        <p className="text-sm text-slate-400 mt-3">
          {hero?.value_recovered_prev_month > 0
            ? `Mês anterior: ${formatCurrency(hero.value_recovered_prev_month)}`
            : "Sem dados de comparação do mês anterior."}
        </p>
      </div>
    </div>
  );
}

/* =============================================================
   FUNNEL — 5 etapas
   ============================================================= */
export function FunnelPanel({ funnel = [] }) {
  if (!funnel.length) return null;
  const max = funnel[0]?.value || 1;
  const colors = [
    "bg-cp-primary",
    "bg-emerald-500",
    "bg-emerald-600",
    "bg-emerald-700",
    "bg-cp-primary-light",
  ];

  return (
    <div className="cp-panel p-5" data-testid="funnel-panel">
      <div className="flex items-center justify-between mb-1">
        <div>
          <div className="text-[11px] uppercase tracking-wider text-slate-500 font-medium">
            funil de recuperação
          </div>
          <div className="font-heading text-lg font-semibold mt-0.5">
            Da geração ao pagamento
          </div>
        </div>
      </div>
      <p className="text-xs text-slate-500 mb-5">
        Quantos PIX passam por cada etapa da operação
      </p>
      <div className="space-y-3">
        {funnel.map((step, i) => {
          const pct = max ? (step.value / max) * 100 : 0;
          const conv =
            i > 0 && funnel[i - 1].value
              ? (step.value / funnel[i - 1].value) * 100
              : null;
          return (
            <div key={step.key} data-testid={`funnel-step-${step.key}`}>
              <div className="flex items-center justify-between text-sm mb-1.5">
                <span className="text-slate-300">{step.label}</span>
                <span className="tabular-nums font-mono text-xs text-slate-400">
                  <span className="font-semibold text-slate-100">
                    {formatNumber(step.value)}
                  </span>
                  {conv != null && (
                    <span className="ml-2 text-cp-primary-light">
                      {conv.toFixed(1)}%
                    </span>
                  )}
                </span>
              </div>
              <div className="h-7 bg-cp-elevated/40 rounded-md overflow-hidden">
                <div
                  className={`h-full ${colors[i] || "bg-cp-primary"} transition-all`}
                  style={{ width: `${Math.max(pct, 4)}%` }}
                />
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

/* =============================================================
   STEP PERFORMANCE — Performance por disparo
   ============================================================= */
export function StepPerformance({ steps = [] }) {
  const labels = {
    1: "1º disparo",
    2: "2º disparo",
    3: "3º disparo",
    4: "4º disparo",
  };
  const subs = {
    1: "≈ 10 min após PIX",
    2: "≈ 30 min",
    3: "≈ 2 horas",
    4: "≈ 24 horas",
  };

  return (
    <div className="cp-panel p-5" data-testid="step-performance">
      <div className="text-[11px] uppercase tracking-wider text-slate-500 font-medium">
        performance por disparo
      </div>
      <div className="font-heading text-lg font-semibold mt-0.5 mb-1">
        Qual mensagem mais converte?
      </div>
      <p className="text-xs text-slate-500 mb-5">
        Recuperações por etapa da sequência (descobre onde vale dobrar a aposta)
      </p>
      {steps.length === 0 ? (
        <div className="text-sm text-slate-500 py-8 text-center">
          Sem dados de disparo ainda. Comece a operação e os números aparecem aqui.
        </div>
      ) : (
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
          {steps.slice(0, 4).map((s) => {
            const idx = (s.step_index || 0) + (s.step_index === 0 ? 1 : 0);
            const showIdx = (s.step_index ?? 0) + 1;
            return (
              <div
                key={idx}
                className="rounded-lg border border-white/[0.06] bg-cp-elevated/20 p-4"
                data-testid={`step-card-${showIdx}`}
              >
                <div className="text-[10px] uppercase tracking-widest text-cp-primary-light font-mono mb-1">
                  {labels[showIdx] || `disparo ${showIdx}`}
                </div>
                <div className="font-heading text-2xl font-bold tabular-nums text-white">
                  {(s.rate || 0).toFixed(1)}%
                </div>
                <div className="text-[11px] text-slate-500 mt-1">
                  {s.recovered}/{s.sent} recuperados
                </div>
                <div className="text-[10px] text-slate-600 mt-0.5">
                  {subs[showIdx] || ""}
                </div>
                {s.value_recovered > 0 && (
                  <div className="text-xs text-cp-primary-light mt-2 font-semibold">
                    {formatCurrency(s.value_recovered)}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

/* =============================================================
   HEALTH PANEL — Saúde da operação
   ============================================================= */
export function HealthPanel({ health }) {
  const wsConnected = health?.whatsapp_status === "connected";
  const wsConnecting = health?.whatsapp_status === "connecting";
  const lastWebhook = health?.last_webhook_at;
  const lastWebhookStr = lastWebhook
    ? formatRelative(lastWebhook)
    : "nunca";

  const items = [
    {
      icon: wsConnected ? Wifi : WifiOff,
      title: "WhatsApp",
      value: wsConnected
        ? "Conectado"
        : wsConnecting
        ? "Conectando..."
        : "Desconectado",
      status: wsConnected ? "ok" : wsConnecting ? "warn" : "bad",
      cta: "/app/connection",
      ctaLabel: wsConnected ? "Ver" : "Conectar",
      testid: "health-whatsapp",
    },
    {
      icon: Plug,
      title: "Última batida do checkout",
      value: lastWebhookStr,
      status: lastWebhook ? "ok" : "warn",
      cta: "/app/integrations",
      ctaLabel: "Integrações",
      testid: "health-webhook",
    },
    {
      icon: Hourglass,
      title: "Jobs na fila",
      value: `${formatNumber(health?.pending_jobs || 0)} agendados`,
      sub: `${health?.jobs_next_1h || 0} em 1h · ${
        health?.jobs_next_6h || 0
      } em 6h · ${health?.jobs_next_24h || 0} em 24h`,
      status: (health?.pending_jobs || 0) > 0 ? "ok" : "neutral",
      cta: "/app/recovery",
      ctaLabel: "Ver",
      testid: "health-jobs",
    },
  ];

  const dot = {
    ok: "bg-cp-primary",
    warn: "bg-cp-warning",
    bad: "bg-red-500",
    neutral: "bg-slate-500",
  };

  return (
    <div className="cp-panel p-5" data-testid="health-panel">
      <div className="text-[11px] uppercase tracking-wider text-slate-500 font-medium mb-4">
        saúde da operação
      </div>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
        {items.map((it) => {
          const Icon = it.icon;
          return (
            <Link
              key={it.title}
              to={it.cta}
              data-testid={it.testid}
              className="group flex items-start gap-3 p-3.5 rounded-lg border border-white/[0.06] bg-cp-elevated/20 hover:border-white/[0.12] transition"
            >
              <div className="h-9 w-9 rounded-md bg-cp-elevated/60 border border-white/[0.06] flex items-center justify-center shrink-0">
                <Icon className="h-4 w-4 text-slate-400" />
              </div>
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-2">
                  <span
                    className={`h-1.5 w-1.5 rounded-full ${dot[it.status]} ${
                      it.status === "ok" ? "animate-pulse" : ""
                    }`}
                  />
                  <span className="text-[11px] uppercase tracking-wider text-slate-500 font-medium truncate">
                    {it.title}
                  </span>
                </div>
                <div className="text-sm font-semibold text-slate-100 mt-1 truncate">
                  {it.value}
                </div>
                {it.sub && (
                  <div className="text-[11px] text-slate-500 mt-0.5 truncate">
                    {it.sub}
                  </div>
                )}
              </div>
              <ChevronRight className="h-4 w-4 text-slate-600 group-hover:text-slate-300 shrink-0" />
            </Link>
          );
        })}
      </div>
    </div>
  );
}

/* =============================================================
   AVG METRICS — Tempo médio + ticket recuperado/perdido
   ============================================================= */
export function AvgMetrics({ avgTimeSec, avgTicketRecovered, avgTicketLost }) {
  const fmtTime = (sec) => {
    if (!sec || sec <= 0) return "—";
    const m = Math.floor(sec / 60);
    if (m < 60) return `${m} min`;
    const h = Math.floor(m / 60);
    const rem = m % 60;
    if (h < 24) return `${h}h ${rem}min`;
    const d = Math.floor(h / 24);
    return `${d}d ${h % 24}h`;
  };
  return (
    <div className="grid grid-cols-1 md:grid-cols-3 gap-3" data-testid="avg-metrics">
      <div className="cp-panel p-5">
        <div className="text-[11px] uppercase tracking-wider text-slate-500 font-medium mb-1">
          tempo médio até pagar
        </div>
        <div className="font-heading text-3xl font-bold tabular-nums text-white">
          {fmtTime(avgTimeSec)}
        </div>
        <div className="text-xs text-slate-500 mt-1">
          do PIX gerado até cair o pagamento
        </div>
      </div>
      <div className="cp-panel p-5">
        <div className="text-[11px] uppercase tracking-wider text-slate-500 font-medium mb-1">
          ticket médio recuperado
        </div>
        <div className="font-heading text-3xl font-bold tabular-nums text-cp-primary-light">
          {formatCurrency(avgTicketRecovered || 0)}
        </div>
        <div className="text-xs text-slate-500 mt-1">
          valor médio das vendas que voltaram
        </div>
      </div>
      <div className="cp-panel p-5">
        <div className="text-[11px] uppercase tracking-wider text-slate-500 font-medium mb-1">
          ticket médio perdido
        </div>
        <div className="font-heading text-3xl font-bold tabular-nums text-cp-warning">
          {formatCurrency(avgTicketLost || 0)}
        </div>
        <div className="text-xs text-slate-500 mt-1">
          PIX pendentes / expirados
        </div>
      </div>
    </div>
  );
}

/* =============================================================
   TOP INTEGRATIONS / TOP PRODUCTS
   ============================================================= */
export function TopIntegrations({ items = [] }) {
  const platLabel = {
    legacy: "Manual / legado",
    simulator: "Simulador",
    cakto: "Cakto",
    syncpay: "SyncPay",
    custom: "Webhook custom",
  };
  return (
    <div className="cp-panel p-5" data-testid="top-integrations">
      <div className="flex items-center justify-between mb-4">
        <div>
          <div className="text-[11px] uppercase tracking-wider text-slate-500 font-medium">
            top integrações
          </div>
          <div className="font-heading text-lg font-semibold mt-0.5">
            Quem traz mais PIX (e quem recupera melhor)
          </div>
        </div>
        <Plug className="h-4 w-4 text-slate-600" />
      </div>
      {!items.length ? (
        <div className="text-sm text-slate-500 py-6 text-center">
          Nenhuma integração ativa no período.
        </div>
      ) : (
        <div className="space-y-2.5">
          {items.map((it, i) => (
            <div
              key={i}
              className="flex items-center justify-between gap-3"
              data-testid={`top-int-${it.platform}`}
            >
              <div className="flex items-center gap-3 min-w-0 flex-1">
                <div className="h-7 w-7 rounded-md bg-cp-primary/10 border border-cp-primary/20 flex items-center justify-center text-[10px] font-bold text-cp-primary-light">
                  {(platLabel[it.platform] || it.platform || "-")
                    .toString()
                    .slice(0, 2)
                    .toUpperCase()}
                </div>
                <div className="min-w-0 flex-1">
                  <div className="text-sm font-medium text-slate-100 truncate">
                    {platLabel[it.platform] || it.platform || "-"}
                  </div>
                  <div className="h-1 mt-1.5 bg-cp-elevated/40 rounded-full overflow-hidden">
                    <div
                      className="h-full bg-cp-primary"
                      style={{ width: `${Math.min(it.rate, 100)}%` }}
                    />
                  </div>
                </div>
              </div>
              <div className="text-right shrink-0">
                <div className="text-sm font-semibold tabular-nums">
                  {it.rate.toFixed(1)}%
                </div>
                <div className="text-[10px] text-slate-500 tabular-nums">
                  {it.recovered}/{it.total}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export function TopProducts({ items = [] }) {
  return (
    <div className="cp-panel p-5" data-testid="top-products">
      <div className="flex items-center justify-between mb-4">
        <div>
          <div className="text-[11px] uppercase tracking-wider text-slate-500 font-medium">
            top produtos
          </div>
          <div className="font-heading text-lg font-semibold mt-0.5">
            Mais vendidos via PIX
          </div>
        </div>
        <Package className="h-4 w-4 text-slate-600" />
      </div>
      {!items.length ? (
        <div className="text-sm text-slate-500 py-6 text-center">
          Sem produtos nomeados nas transações ainda.
        </div>
      ) : (
        <div className="space-y-2.5">
          {items.map((it, i) => (
            <div
              key={i}
              className="flex items-center justify-between gap-3"
              data-testid={`top-prod-${i}`}
            >
              <div className="min-w-0 flex-1">
                <div className="text-sm text-slate-100 truncate font-medium">
                  {it.product || "-"}
                </div>
                <div className="text-[11px] text-slate-500 tabular-nums">
                  {formatNumber(it.total)} PIX · {it.recovered} recuperados ·{" "}
                  {formatCurrency(it.value_recovered)}
                </div>
              </div>
              <div className="text-right shrink-0">
                <div className="text-sm font-semibold tabular-nums">
                  {it.rate.toFixed(1)}%
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

/* =============================================================
   MESSAGES PANEL — sent / failed / skipped
   ============================================================= */
export function MessagesPanel({ today, period, deliveryRate }) {
  const total =
    (period?.sent || 0) + (period?.failed || 0) + (period?.skipped || 0);
  const sentPct = total ? ((period?.sent || 0) / total) * 100 : 0;
  return (
    <div className="cp-panel p-5" data-testid="messages-panel">
      <div className="flex items-center justify-between mb-4">
        <div>
          <div className="text-[11px] uppercase tracking-wider text-slate-500 font-medium">
            mensagens
          </div>
          <div className="font-heading text-lg font-semibold mt-0.5">
            Saúde do envio
          </div>
        </div>
        <Send className="h-4 w-4 text-slate-600" />
      </div>

      <div className="grid grid-cols-3 gap-2 mb-4">
        <div
          className="rounded-lg border border-white/[0.06] bg-cp-elevated/20 px-3 py-3"
          data-testid="msg-sent"
        >
          <div className="text-[10px] uppercase tracking-wider text-slate-500 font-mono">
            enviadas
          </div>
          <div className="font-heading text-2xl font-bold text-cp-primary-light tabular-nums">
            {formatNumber(period?.sent || 0)}
          </div>
          <div className="text-[10px] text-slate-500 mt-0.5">
            hoje: {formatNumber(today?.sent || 0)}
          </div>
        </div>
        <div
          className="rounded-lg border border-white/[0.06] bg-cp-elevated/20 px-3 py-3"
          data-testid="msg-failed"
        >
          <div className="text-[10px] uppercase tracking-wider text-slate-500 font-mono">
            falhas
          </div>
          <div className="font-heading text-2xl font-bold text-red-400 tabular-nums">
            {formatNumber(period?.failed || 0)}
          </div>
          <div className="text-[10px] text-slate-500 mt-0.5">
            hoje: {formatNumber(today?.failed || 0)}
          </div>
        </div>
        <div
          className="rounded-lg border border-white/[0.06] bg-cp-elevated/20 px-3 py-3"
          data-testid="msg-skipped"
        >
          <div className="text-[10px] uppercase tracking-wider text-slate-500 font-mono">
            puladas
          </div>
          <div className="font-heading text-2xl font-bold text-cp-warning tabular-nums">
            {formatNumber(period?.skipped || 0)}
          </div>
          <div className="text-[10px] text-slate-500 mt-0.5">
            hoje: {formatNumber(today?.skipped || 0)}
          </div>
        </div>
      </div>

      <div className="flex items-center justify-between text-xs mb-1.5">
        <span className="text-slate-400">Taxa de entrega</span>
        <span className="tabular-nums font-semibold text-cp-primary-light">
          {(deliveryRate || 0).toFixed(1)}%
        </span>
      </div>
      <div className="h-1.5 bg-cp-elevated/40 rounded-full overflow-hidden">
        <div
          className="h-full bg-cp-primary"
          style={{ width: `${Math.min(sentPct, 100)}%` }}
        />
      </div>
    </div>
  );
}

/* =============================================================
   HEATMAP — Recuperações por hora x dia da semana
   ============================================================= */
export function HeatmapPanel({ data = [] }) {
  // dow 1=Sun..7=Sat (Mongo)
  const dowLabels = ["Dom", "Seg", "Ter", "Qua", "Qui", "Sex", "Sáb"];
  const matrix = Array.from({ length: 7 }, () => Array(24).fill(0));
  let max = 0;
  for (const cell of data) {
    const dowIdx = ((cell.dow || 1) - 1 + 7) % 7;
    const h = cell.hour || 0;
    matrix[dowIdx][h] += cell.count || 0;
    if (matrix[dowIdx][h] > max) max = matrix[dowIdx][h];
  }
  const intensity = (v) => {
    if (!max || !v) return 0;
    return Math.min(v / max, 1);
  };

  return (
    <div className="cp-panel p-5" data-testid="heatmap-panel">
      <div className="flex items-center justify-between mb-4">
        <div>
          <div className="text-[11px] uppercase tracking-wider text-slate-500 font-medium">
            heatmap de recuperação
          </div>
          <div className="font-heading text-lg font-semibold mt-0.5">
            Quando o cliente paga (hora × dia)
          </div>
          <div className="text-xs text-slate-500 mt-0.5">
            Quanto mais verde, mais recuperações naquele horário
          </div>
        </div>
        <Clock className="h-4 w-4 text-slate-600" />
      </div>
      {!data.length ? (
        <div className="text-sm text-slate-500 py-6 text-center">
          Sem recuperações suficientes pra montar o mapa ainda.
        </div>
      ) : (
        <div className="overflow-x-auto">
          <div className="inline-block min-w-full">
            <div className="flex gap-1 mb-1 ml-10">
              {Array.from({ length: 24 }).map((_, h) => (
                <div
                  key={h}
                  className={`w-4 text-[8px] text-slate-600 text-center font-mono ${
                    h % 3 === 0 ? "" : "opacity-30"
                  }`}
                  style={{ minWidth: "16px" }}
                >
                  {h}
                </div>
              ))}
            </div>
            {dowLabels.map((d, dow) => (
              <div key={d} className="flex gap-1 items-center mb-1">
                <div className="w-9 text-[10px] text-slate-500 font-mono text-right pr-1">
                  {d}
                </div>
                {Array.from({ length: 24 }).map((_, h) => {
                  const v = matrix[dow][h];
                  const a = intensity(v);
                  return (
                    <div
                      key={h}
                      title={`${d} ${h}h - ${v} recuperações`}
                      className="w-4 h-4 rounded-sm border border-white/[0.04]"
                      style={{
                        minWidth: "16px",
                        backgroundColor:
                          a > 0
                            ? `rgba(34, 197, 94, ${0.18 + a * 0.7})`
                            : "rgba(255,255,255,0.02)",
                      }}
                    />
                  );
                })}
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

/* =============================================================
   RECENT TRANSACTIONS — PIX em tempo real
   ============================================================= */
export function RecentTransactions({ items = [] }) {
  const statusBadge = (tx) => {
    if (tx.status === "paid" && tx.recovered)
      return {
        label: "Recuperado",
        cls: "bg-cp-primary/15 text-cp-primary-light border-cp-primary/30",
        Icon: CheckCircle2,
      };
    if (tx.status === "paid")
      return {
        label: "Pago",
        cls: "bg-emerald-900/30 text-emerald-300 border-emerald-700/30",
        Icon: CheckCircle2,
      };
    if (tx.status === "expired")
      return {
        label: "Expirado",
        cls: "bg-red-900/30 text-red-300 border-red-700/30",
        Icon: XCircle,
      };
    if (tx.status === "pending")
      return {
        label: "Em recuperação",
        cls: "bg-cp-warning/15 text-cp-warning border-cp-warning/30",
        Icon: Activity,
      };
    return {
      label: tx.status,
      cls: "bg-slate-800 text-slate-300 border-slate-700",
      Icon: AlertTriangle,
    };
  };

  return (
    <div className="cp-panel p-5" data-testid="recent-transactions">
      <div className="flex items-center justify-between mb-4">
        <div>
          <div className="text-[11px] uppercase tracking-wider text-slate-500 font-medium">
            PIX em tempo real
          </div>
          <div className="font-heading text-lg font-semibold mt-0.5">
            Últimas 15 transações
          </div>
        </div>
        <Link
          to="/app/transactions"
          data-testid="recent-tx-view-all"
          className="text-xs text-cp-primary-light hover:text-white inline-flex items-center gap-1"
        >
          Ver tudo <ChevronRight className="h-3.5 w-3.5" />
        </Link>
      </div>
      {!items.length ? (
        <div className="text-sm text-slate-500 py-8 text-center">
          Sem transações ainda. Conecte um checkout pra começar.
        </div>
      ) : (
        <div className="divide-y divide-white/[0.04]">
          {items.map((tx) => {
            const b = statusBadge(tx);
            return (
              <Link
                key={tx.id}
                to={`/app/transactions?tx=${tx.id}`}
                data-testid={`recent-tx-${tx.id}`}
                className="flex items-center gap-3 py-2.5 hover:bg-white/[0.02] -mx-2 px-2 rounded transition"
              >
                <div className="h-8 w-8 rounded-md bg-cp-elevated/40 border border-white/[0.06] flex items-center justify-center shrink-0">
                  <b.Icon className="h-3.5 w-3.5 text-slate-400" />
                </div>
                <div className="min-w-0 flex-1">
                  <div className="text-sm font-medium text-slate-100 truncate">
                    {tx.customer_name || tx.customer_phone || tx.external_id}
                  </div>
                  <div className="text-[11px] text-slate-500 truncate">
                    {tx.product_name ? `${tx.product_name} · ` : ""}
                    {tx.platform || "manual"} · {formatRelative(tx.created_at)}
                  </div>
                </div>
                <div className="text-right shrink-0">
                  <div className="text-sm font-semibold tabular-nums">
                    {formatCurrency(tx.amount)}
                  </div>
                  <span
                    className={`inline-block mt-0.5 text-[9px] uppercase tracking-wider font-mono px-1.5 py-0.5 rounded border ${b.cls}`}
                  >
                    {b.label}
                  </span>
                </div>
              </Link>
            );
          })}
        </div>
      )}
    </div>
  );
}

/* =============================================================
   UPCOMING JOBS — Próximos disparos agendados
   ============================================================= */
export function UpcomingJobs({ health }) {
  return (
    <div className="cp-panel p-5" data-testid="upcoming-jobs">
      <div className="flex items-center justify-between mb-4">
        <div>
          <div className="text-[11px] uppercase tracking-wider text-slate-500 font-medium">
            próximos disparos
          </div>
          <div className="font-heading text-lg font-semibold mt-0.5">
            O que vai sair da fila
          </div>
        </div>
        <Zap className="h-4 w-4 text-slate-600" />
      </div>
      <div className="grid grid-cols-3 gap-2">
        {[
          { label: "Próxima 1h", value: health?.jobs_next_1h, hint: "iminentes" },
          { label: "Próximas 6h", value: health?.jobs_next_6h, hint: "curto prazo" },
          {
            label: "Próximas 24h",
            value: health?.jobs_next_24h,
            hint: "dia inteiro",
          },
        ].map((b, i) => (
          <div
            key={i}
            className="rounded-lg border border-white/[0.06] bg-cp-elevated/20 p-3"
          >
            <div className="text-[10px] uppercase tracking-wider text-slate-500 font-mono">
              {b.label}
            </div>
            <div className="font-heading text-2xl font-bold tabular-nums text-white">
              {formatNumber(b.value || 0)}
            </div>
            <div className="text-[10px] text-slate-600 mt-0.5">{b.hint}</div>
          </div>
        ))}
      </div>
      <div className="mt-3 text-xs text-slate-500">
        Total na fila:{" "}
        <span className="text-slate-300 font-semibold">
          {formatNumber(health?.pending_jobs || 0)}
        </span>{" "}
        jobs pendentes
      </div>
    </div>
  );
}
